/**
 * Typed fetch client for the STR Wiki API.
 *
 * - Same-origin: all paths are relative (`/api/*`).
 * - Sends cookies (`credentials: 'include'`) so the session cookie rides along.
 * - Throws `ApiError` on any non-2xx response, with the parsed error body.
 *
 * Import as: `import { api } from '@/lib/api';`
 */
import type {
  Activity,
  AdminUser,
  AskResult,
  Comment,
  CommentAnchor,
  InboxComment,
  NodeDoc,
  NodeType,
  SearchResult,
  Section,
  TempPasswordResult,
  TipTapDoc,
  Tree,
  User,
} from '@/lib/types';

/** Thrown for any non-2xx API response. */
export class ApiError extends Error {
  readonly status: number;
  /** The parsed JSON error body, if any (e.g. `{ error: 'stale', current }`). */
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /** True for the stale-write conflict from PUT /api/nodes/{id}. */
  get isStale(): boolean {
    return (
      this.status === 409 &&
      typeof this.body === 'object' &&
      this.body !== null &&
      (this.body as { error?: string }).error === 'stale'
    );
  }
}

type Json = Record<string, unknown> | unknown[];

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Best-effort human-readable message from a parsed error body. */
function errorMessage(parsed: unknown, res: Response): string {
  if (parsed && typeof parsed === 'object') {
    const o = parsed as { message?: unknown; error?: unknown };
    const m = o.message ?? o.error;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return res.statusText || `Request failed (${res.status})`;
}

async function request<T>(
  method: string,
  path: string,
  body?: Json | FormData,
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'include',
  };

  if (body instanceof FormData) {
    // Let the browser set the multipart boundary.
    init.body = body;
  } else if (body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }

  const res = await fetch(path, init);
  const parsed = await parseBody(res);

  if (!res.ok) {
    throw new ApiError(res.status, errorMessage(parsed, res), parsed);
  }

  return parsed as T;
}

export const api = {
  // ---- Auth ----
  login(email: string, password: string): Promise<User> {
    return request<User>('POST', '/api/auth/login', { email, password });
  },
  logout(): Promise<{ ok: true }> {
    return request<{ ok: true }>('POST', '/api/auth/logout');
  },
  /** Current user; throws ApiError(401) when not authenticated. */
  me(): Promise<User> {
    return request<User>('GET', '/api/me');
  },
  changePassword(currentPassword: string, newPassword: string): Promise<{ ok: true }> {
    return request<{ ok: true }>('POST', '/api/auth/change-password', {
      currentPassword,
      newPassword,
    });
  },
  /** Request a reset email. Always resolves {ok:true} regardless of whether the account exists. */
  requestPasswordReset(email: string): Promise<{ ok: true }> {
    return request<{ ok: true }>('POST', '/api/auth/forgot-password', { email });
  },
  /** Complete a reset with the emailed token. Throws ApiError(400) if the link is invalid/expired. */
  resetPassword(token: string, newPassword: string): Promise<{ ok: true }> {
    return request<{ ok: true }>('POST', '/api/auth/reset-password', { token, newPassword });
  },

  // ---- Tree & nodes ----
  tree(): Promise<Tree> {
    return request<Tree>('GET', '/api/tree');
  },
  getNode(id: string): Promise<NodeDoc> {
    return request<NodeDoc>('GET', `/api/nodes/${encodeURIComponent(id)}`);
  },
  createNode(input: {
    sectionId: string;
    parentId: string | null;
    type: NodeType;
    title: string;
    body?: TipTapDoc | null;
    contentType?: 'tiptap' | 'html';
    html?: string;
  }): Promise<NodeDoc> {
    return request<NodeDoc>('POST', '/api/nodes', input);
  },
  /**
   * Update a node. On a concurrent edit the API returns 409 with
   * `{ error: 'stale', current }` — caught as `ApiError` (see `.isStale`).
   */
  updateNode(
    id: string,
    input: {
      title?: string;
      /** Status badge label; empty string clears it. Omit to leave unchanged. */
      status?: string;
      body?: TipTapDoc | null;
      contentType?: 'tiptap' | 'html';
      html?: string;
      expectedEtag?: string;
    },
  ): Promise<NodeDoc> {
    return request<NodeDoc>('PUT', `/api/nodes/${encodeURIComponent(id)}`, input);
  },
  /**
   * Re-parent and re-order a node (sidebar drag-and-drop) — within its section, or
   * across sections when `sectionId` names a different destination (the node and its
   * whole subtree are relocated server-side; ids stay stable).
   *
   * `order` is the destination parent's full child list, in the order the client just
   * rendered it, including the moved node — the server writes it out as position 0..n-1.
   * The client sends the whole list rather than an index because until the first drag,
   * every node in the DB has position 0, so an index alone is meaningless.
   *
   * Separate from updateNode() because PUT snapshots a version on every call.
   */
  moveNode(
    id: string,
    input: { parentId: string | null; sectionId?: string; order: string[] },
  ): Promise<NodeDoc> {
    return request<NodeDoc>('POST', `/api/nodes/${encodeURIComponent(id)}/move`, input);
  },
  /** Archive a node — it moves into the collapsed "Archived" group of its section. */
  archiveNode(id: string): Promise<{ id: string }> {
    return request<{ id: string }>('POST', `/api/nodes/${encodeURIComponent(id)}/archive`);
  },
  /** Un-archive / un-trash a node (clears both flags). */
  restoreNode(id: string): Promise<{ id: string }> {
    return request<{ id: string }>('POST', `/api/nodes/${encodeURIComponent(id)}/restore`);
  },
  /**
   * Soft-delete a node. `children` says what happens to its sub-items:
   * 'reparent' (default) slots them under the grandparent; 'delete' cascades.
   */
  trashNode(
    id: string,
    children: 'delete' | 'reparent' = 'reparent',
  ): Promise<{ id: string; removed: number }> {
    return request<{ id: string; removed: number }>(
      'POST',
      `/api/nodes/${encodeURIComponent(id)}/trash`,
      { children },
    );
  },

  // ---- Users ----
  /**
   * The roster — email + display name, for any signed-in user. Distinct from
   * `adminUsers()`, which is admin-only; assignment needs a list everyone can read.
   */
  users(): Promise<Array<{ email: string; displayName: string }>> {
    return request<Array<{ email: string; displayName: string }>>('GET', '/api/users');
  },

  // ---- Comments ----
  comments(nodeId: string): Promise<Comment[]> {
    return request<Comment[]>('GET', `/api/comments?nodeId=${encodeURIComponent(nodeId)}`);
  },
  addComment(input: {
    nodeId: string;
    body: string;
    parentId?: string;
    assignee?: string | null;
    /** Quote captured by highlighting the page. Ignored on a reply. */
    anchor?: CommentAnchor;
    blockId?: string;
  }): Promise<Comment> {
    return request<Comment>('POST', '/api/comments', input);
  },
  /** Resolve/reopen, reassign, or edit. Only the fields you pass are touched. */
  updateComment(
    id: string,
    patch: { resolved?: boolean; assignee?: string | null; body?: string },
  ): Promise<Comment> {
    return request<Comment>('PATCH', `/api/comments/${encodeURIComponent(id)}`, patch);
  },
  deleteComment(id: string): Promise<{ id: string; deleted: boolean }> {
    return request<{ id: string; deleted: boolean }>(
      'DELETE',
      `/api/comments/${encodeURIComponent(id)}`,
    );
  },
  /** Open threads assigned to someone — defaults to the signed-in user. */
  commentInbox(assignee?: string): Promise<InboxComment[]> {
    const q = assignee ? `?assignee=${encodeURIComponent(assignee)}` : '';
    return request<InboxComment[]>('GET', `/api/comments/inbox${q}`);
  },

  // ---- Feed ----
  feed(): Promise<Activity[]> {
    return request<Activity[]>('GET', '/api/feed');
  },

  // ---- Search & Ask ----
  search(q: string): Promise<{ results: SearchResult[] }> {
    return request<{ results: SearchResult[] }>('POST', '/api/search', { q });
  },
  ask(q: string): Promise<AskResult> {
    return request<AskResult>('POST', '/api/ask', { q });
  },

  // ---- Upload ----
  upload(file: File): Promise<{ url: string }> {
    const form = new FormData();
    form.append('file', file);
    return request<{ url: string }>('POST', '/api/upload', form);
  },

  // ---- Sections ----
  sections(): Promise<Section[]> {
    return request<Section[]>('GET', '/api/sections');
  },

  // ---- Admin ----
  adminUsers(): Promise<AdminUser[]> {
    return request<AdminUser[]>('GET', '/api/mgmt/users');
  },
  adminCreateUser(input: {
    email: string;
    name: string;
    isAdmin: boolean;
  }): Promise<TempPasswordResult> {
    return request<TempPasswordResult>('POST', '/api/mgmt/users', input);
  },
  adminResetUser(email: string): Promise<TempPasswordResult> {
    return request<TempPasswordResult>(
      'POST',
      `/api/mgmt/users/${encodeURIComponent(email)}/reset`,
    );
  },
  adminCreateSection(name: string): Promise<Section> {
    return request<Section>('POST', '/api/mgmt/sections', { name });
  },
  /** Re-order header sections (full id list, rendered order). Admin only. */
  adminReorderSections(order: string[]): Promise<{ order: string[] }> {
    return request<{ order: string[] }>('PUT', '/api/mgmt/sections/order', { order });
  },
  /** Delete a header section; its pages move to the "No Header" catch-all. Admin only. */
  adminDeleteSection(id: string): Promise<{ id: string; moved: number }> {
    return request<{ id: string; moved: number }>(
      'DELETE',
      `/api/mgmt/sections/${encodeURIComponent(id)}`,
    );
  },
  adminSetSectionMembers(id: string, emails: string[]): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      'PUT',
      `/api/mgmt/sections/${encodeURIComponent(id)}/members`,
      { emails },
    );
  },
  /** Current member emails for a section (empty array = open to everyone). */
  adminGetSectionMembers(id: string): Promise<{ emails: string[] }> {
    return request<{ emails: string[] }>(
      'GET',
      `/api/mgmt/sections/${encodeURIComponent(id)}/members`,
    );
  },

  // ---- Version history ----
  getVersions(
    nodeId: string,
  ): Promise<Array<{ versionNum: number; title: string; editedBy: string | null; editedAt: string }>> {
    return request('GET', `/api/nodes/${encodeURIComponent(nodeId)}/versions`);
  },
  getVersion(nodeId: string, versionNum: number): Promise<NodeDoc & { versionNum: number }> {
    return request('GET', `/api/nodes/${encodeURIComponent(nodeId)}/versions/${versionNum}`);
  },
  restoreVersion(nodeId: string, versionNum: number): Promise<NodeDoc> {
    return request('POST', `/api/nodes/${encodeURIComponent(nodeId)}/versions/${versionNum}/restore`);
  },
};

export type Api = typeof api;

// Also available as a default import: `import api from '@/lib/api'`.
export default api;
